import React from "react";

import { Outcome } from "./Outcome";
import { getOutcomeCompetitor, type OutcomeLayoutProps } from "./outcomeLayout";

import * as styles from "./Outcomes.module.scss";

type HeadToHeadRowProps = OutcomeLayoutProps & {
  // both optional - a team filter can leave just one side (+ maybe the
  // draw). At least one of home/away is guaranteed by the caller
  homeOutcomeId?: string;
  awayOutcomeId?: string;
  drawOutcomeId?: string;
  roundBottom?: boolean;
  bottomBorder?: boolean;
};

// home / (draw) / away on a single row - also used per group by winning
// margin, hence roundBottom
const HeadToHeadRow = ({
  outcomes,
  competitors,
  eventId,
  eventStatus,
  marketId,
  oddsFormat,
  selections,
  toggleSelection,
  isSuspended = false,
  status,
  isLoading = false,
  mainMarket,
  homeOutcomeId,
  awayOutcomeId,
  drawOutcomeId,
  roundBottom = true,
  bottomBorder = false,
  renderFieldsKey,
}: HeadToHeadRowProps) => {
  // middle outcome isn't always typed DRAW - label a copy, not the original,
  // since winning margin renders many of these
  const rawDrawOutcome = drawOutcomeId ? outcomes[drawOutcomeId] : null;
  const drawOutcome = rawDrawOutcome
    ? { ...rawDrawOutcome, type: "DRAW" as const }
    : null;

  const hasDraw = !!(drawOutcomeId && drawOutcome);
  const isHomeLast = !hasDraw && !awayOutcomeId;
  const isDrawFirst = !homeOutcomeId;
  const isDrawLast = !awayOutcomeId;
  const isAwayFirst = !homeOutcomeId && !hasDraw;

  return (
    <>
      {homeOutcomeId && (
        <Outcome
          id={homeOutcomeId}
          eventId={eventId}
          eventStatus={eventStatus}
          marketId={marketId}
          competitor={getOutcomeCompetitor(
            competitors,
            outcomes[homeOutcomeId],
            mainMarket,
          )}
          className={styles.outcome}
          align="right"
          outcome={outcomes[homeOutcomeId]}
          oddsFormat={oddsFormat}
          isSelected={selections.includes(homeOutcomeId)}
          toggleSelection={toggleSelection}
          isSuspended={isSuspended}
          status={status}
          isHome
          edges={{
            roundBottomLeft: roundBottom,
            roundBottomRight: roundBottom && isHomeLast,
            bottomBorder: bottomBorder && isHomeLast,
          }}
          hasDraw={hasDraw}
          isLoading={isLoading}
          renderFieldsKey={renderFieldsKey}
        />
      )}
      {drawOutcomeId && drawOutcome && (
        <Outcome
          id={drawOutcomeId}
          isDraw
          eventId={eventId}
          eventStatus={eventStatus}
          marketId={marketId}
          competitor={null}
          className={styles.outcome}
          align="center"
          outcome={drawOutcome}
          oddsFormat={oddsFormat}
          isSelected={selections.includes(drawOutcomeId)}
          toggleSelection={toggleSelection}
          isSuspended={isSuspended}
          status={status}
          // each tile after the first draws the divider on its left
          edges={{
            roundBottomLeft: roundBottom && isDrawFirst,
            roundBottomRight: roundBottom && isDrawLast,
            dividerLeft: !isDrawFirst,
            bottomBorder: bottomBorder && isDrawLast,
          }}
          hasDraw={hasDraw}
          isLoading={isLoading}
          renderFieldsKey={renderFieldsKey}
        />
      )}
      {awayOutcomeId && (
        <Outcome
          id={awayOutcomeId}
          eventId={eventId}
          eventStatus={eventStatus}
          marketId={marketId}
          competitor={getOutcomeCompetitor(
            competitors,
            outcomes[awayOutcomeId],
            mainMarket,
          )}
          className={styles.outcome}
          align="left"
          outcome={outcomes[awayOutcomeId]}
          oddsFormat={oddsFormat}
          isSelected={selections.includes(awayOutcomeId)}
          toggleSelection={toggleSelection}
          isSuspended={isSuspended}
          status={status}
          isAway
          edges={{
            roundBottomLeft: roundBottom && isAwayFirst,
            roundBottomRight: roundBottom,
            dividerLeft: !isAwayFirst,
            bottomBorder,
          }}
          hasDraw={hasDraw}
          isLoading={isLoading}
          renderFieldsKey={renderFieldsKey}
        />
      )}
    </>
  );
};

export { HeadToHeadRow };
